import type {Route} from '@prismicio/client';
import type {KeyTextField, LinkField} from '@prismicio/types';
import {useGuestHost, useLocalePrefix, usePrismicTypeGuards} from '#imports';
import {useRouteHelper} from '@malt/utils';

interface Doc {
    lang: string;
    type: string;
    uid?: string | null;
}

const CMS_PREFIX = 'c';
const RC_PREFIX = 'resources';

export const routes: Route[] = [
    {
        type: 'custom-page',
        path: `/${CMS_PREFIX}/:uid`,
    },
    {
        type: 'homepage',
        path: '/',
    },
    {
        type: 'companies-parent-page',
        path: `/${CMS_PREFIX}/companies`,
    },
    {
        type: 'companies-child-page',
        path: `/${CMS_PREFIX}/companies/:uid`,
    },
    {
        type: 'solutions-parent-page',
        path: `/${CMS_PREFIX}/solutions`,
    },
    {
        type: 'solutions-child-page',
        path: `/${CMS_PREFIX}/solutions/:uid`,
    },
    {
        type: 'freelancers-parent-page',
        path: `/${CMS_PREFIX}/freelancers`,
    },
    {
        type: 'freelancers-child-page',
        path: `/${CMS_PREFIX}/freelancers/:uid`,
    },
    {
        type: 'resources-parent-page',
        path: `/${CMS_PREFIX}/resources`,
    },
    {
        type: 'maltStrategyChildPage',
        path: `/${CMS_PREFIX}/maltstrategy/:uid`,
    },
    {
        type: 'maltStrategyParentPage',
        path: `/${CMS_PREFIX}/maltstrategy`,
    },
    {
        type: 'productFeature',
        path: `/platform/:uid`,
    },
    {
        type: 'resources-child-page',
        path: `/${CMS_PREFIX}/resources/:uid`,
    },
    {
        type: 'resourcesCenterHome',
        path: `/${RC_PREFIX}`,
    },
    {type: 'homeArticles', path: `/${RC_PREFIX}/articles`},
    {
        type: 'article',
        path: `/${RC_PREFIX}/article/:uid`,
    },
    {type: 'homePressReleases', path: `/${RC_PREFIX}/press-releases`},
    {
        type: 'pressRelease',
        path: `/${RC_PREFIX}/press-release/:uid`,
    },
    {type: 'homeEvents', path: `/${RC_PREFIX}/events`},
    {type: 'homeSuccessStories', path: `/${RC_PREFIX}/success-stories`},
    {
        type: 'successStory',
        path: `/${RC_PREFIX}/success-story/:uid`,
    },
    {type: 'homeGuides', path: `/${RC_PREFIX}/guides`},
    {
        type: 'guide',
        path: `/${RC_PREFIX}/guide/:uid`,
    },
    {
        type: 'collection',
        path: `/${RC_PREFIX}/collection/:uid`,
    },
    {
        type: 'homeCollections',
        path: `/${RC_PREFIX}/collections`,
    },
    {
        type: 'event',
        path: `/${RC_PREFIX}/event/:uid`,
    },
    {
        type: 'trends',
        path: `/${RC_PREFIX}/trends/:uid`,
    },
];

function prefixIfInternal(path: string, withLocalePrefix: (path: string) => string): string {
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
        return path;
    }
    return withLocalePrefix(path);
}

function linkTextAndTarget(field: object): {text: string; target?: string} {
    const text = 'text' in field && typeof field.text === 'string' ? field.text : '';
    const target = 'target' in field && typeof field.target === 'string' ? field.target : undefined;
    return {text, target};
}

export function useLinkHelper() {
    const host = useGuestHost();
    const {withLocalePrefix} = useLocalePrefix();
    const {isLocaleRoute} = useRouteHelper();
    const {isFilledLink, isFilledWebLink, isFilledKeyText, isFilledContentRelationshipField} = usePrismicTypeGuards();

    function resolvePrismicLink(doc: Doc): string {
        const route = routes.find((prismicRoute) => prismicRoute.type === doc.type);

        if (!route) {
            return host;
        }

        const pathPattern = route.path.replace(':uid', ':uid()');
        const path = withLocalePrefix(route.path.replace(':uid', doc.uid || ''));

        if (isLocaleRoute(pathPattern)) {
            return path;
        }

        return host + path;
    }

    function getLink(field: LinkField | KeyTextField) {
        if (isFilledKeyText(field)) {
            return {
                url: prefixIfInternal(field, withLocalePrefix),
                text: '',
                target: undefined,
            };
        }
        if (field && isFilledWebLink(field)) {
            return {
                url: field.url,
                ...linkTextAndTarget(field),
            };
        }
        if (field && isFilledContentRelationshipField(field)) {
            return {
                url: resolvePrismicLink(field),
                ...linkTextAndTarget(field),
            };
        }
        // Any other filled link exposing a url, such as Media.
        if (field && isFilledLink(field) && typeof field.url === 'string') {
            return {
                url: field.url,
                ...linkTextAndTarget(field),
            };
        }
        return null;
    }

    return {resolvePrismicLink, getLink};
}
